import { MYNAVI_PRIMARY } from '@/components/Campaign/mynavi/Color'
import { MyNaviButton } from '@/components/Campaign/mynavi/MyNaviButton/MyNaviButton'
import { MyNaviDialog } from '@/components/Campaign/mynavi/MyNaviDialog/MyNaviDialog'
import { EmailRegistrationDialogs } from '@/components/Campaign/mynavi/MyNaviDialogs/EmailRegistrationDialogs'
import { MyNaviInput } from '@/components/Campaign/mynavi/MyNaviInput/MyNaviInput'
import { MyNaviMenuDrawer } from '@/components/Campaign/mynavi/MyNaviMenuDrawer/MyNaviMenuDrawer'
import { MyNaviNearbyOcDialog } from '@/components/Campaign/mynavi/MyNaviNearbyOcDialog/MyNaviNearbyOcDialog'
import { MyNaviParticipateButton } from '@/components/Campaign/mynavi/MyNaviParticipateButton/MyNaviParticipateButton'
import { MyNaviPointDisplay } from '@/components/Campaign/mynavi/MyNaviPointDisplay/MyNaviPointDisplay'
import { MyNaviTypography } from '@/components/Campaign/mynavi/MyNaviUI/MyNaviTypography'
import {
  type MyNaviNavigationItem,
  MyNaviVerticalNavigation,
} from '@/components/Campaign/mynavi/MyNaviVerticalNavigation/MyNaviVerticalNavigation'
import { Checkbox } from '@/components/UI/Checkbox'
import { useApi } from '@/core/api/useApi'
import { useTanstackCampaignFetchFn, useTanstackMenuFetchFn } from '@/core/api/useTanstackFetchFn'
import { useCampaignSlug } from '@/core/app/useCampaignSlug'
import { CollegeDetail } from '@/features/pages/mynavi/CollegeDetail'
import { SearchResult } from '@/features/pages/mynavi/SearchResult'
import type { MenuData } from '@/schemas/v2'
import { useTokenStore } from '@/store/useTokenStore'
import { useDebouncedValue } from '@mantine/hooks'
import { IconBriefcase, IconBuilding, IconCalendar, IconMapPin, IconMenu2, IconSchool } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FilterArea } from './filter/FilterArea'
import { FilterJobCategory } from './filter/FilterJobCategory'
import { FilterPeriod } from './filter/FilterPeriod'
import { FilterSchoolType } from './filter/FilterSchoolType'
import { FilterStudyField } from './filter/FilterStudyField'
import { useMynaviHomeEmailStateMachine } from './useMynaviHomeEmailStateMachine'
import { type FilterId, useMynaviStateMachine } from './useMynaviStateMachine'
import { buildFilterParams } from './utils'

// エリア選択の表示名生成（例: "大阪市、堺市 その他2件"）
const MAX_DISPLAY_NAMES = 2

function buildNamesLabel(names: string[]): string {
  if (names.length === 0) return ''
  if (names.length <= MAX_DISPLAY_NAMES) return names.join('、')
  return `${names.slice(0, MAX_DISPLAY_NAMES).join('、')} その他${names.length - MAX_DISPLAY_NAMES}件`
}

// id/name/children を持つツリー（MynaviStudyFieldNode / MynaviJobCategoryNode 等）を
// 再帰的に走査し id → name の逆引きマップを作る。ジェネリクスで各スキーマ型をそのまま受け取る。
function buildIdNameMap<T extends { id: number; name: string; children: T[] | null }>(nodes: T[]): Map<number, string> {
  const map = new Map<number, string>()
  const walk = (list: T[]) => {
    for (const node of list) {
      map.set(node.id, node.name)
      if (node.children) walk(node.children)
    }
  }
  walk(nodes)
  return map
}

export const MyNaviHome = () => {
  const router = useRouter()
  const { t } = useTranslation()
  const { loginUrl, gen, LOCAL_STORAGE_POPUP_HOOKPOINT_KEY } = useCampaignSlug()
  const idToken = useTokenStore((s) => s.idToken)
  const tokenLoading = useTokenStore((s) => s.loading)
  const api = useApi()
  const [schoolName, setSchoolName] = useState('')
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  // 画面最下部までスクロールしたか（参加登録 FAB をクリアボタンと重ならないよう上方へ退避させる用）
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false)

  useEffect(() => {
    // 最下部付近（残り 60px 以内）に到達したら FAB を上方へ退避
    const BOTTOM_THRESHOLD = 60
    const handleScroll = () => {
      const scrolledToBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - BOTTOM_THRESHOLD
      // 値が変化したときのみ更新（scroll は高頻度発火のため不要な state update を抑制）
      setIsScrolledToBottom((prev) => (prev === scrolledToBottom ? prev : scrolledToBottom))
    }
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
    }
  }, [])

  // /api/v2/campaign からキャンペーン情報（トップ画像等）を取得（キャンペーン識別はリクエストヘッダーの campaignSlug で行われる）
  const campaignFetchFn = useTanstackCampaignFetchFn()
  const { data: campaignData } = useQuery(campaignFetchFn)
  const topImage = campaignData?.campaign?.image ?? null

  // /api/v2/menus からメニュー項目を取得
  const menuFetchFn = useTanstackMenuFetchFn()
  const { data: menuData } = useQuery(menuFetchFn)
  const apiMenuItems = useMemo(
    () =>
      (menuData?.menus ?? []).filter(
        (item): item is Extract<MenuData, { type: 'URL' | 'STATIC' }> => item.type === 'URL' || item.type === 'STATIC',
      ),
    [menuData?.menus],
  )

  // ポイント残高取得（ログイン済みのみ）
  const { data: userSheetsData, isLoading: userSheetsLoading } = useQuery({
    queryKey: ['mynavi', 'user-sheets'],
    queryFn: () => api.auth.fetchUserSheets(),
    enabled: !!idToken && !tokenLoading,
    staleTime: 60_000,
  })

  const {
    send,
    waitForQuestionnaireSubmitted,
    isOcConfirmDialog,
    isOcNearbyOcDialog,
    isOcAlreadyParticipated,
    isOcStampAlreadyAcquiredToday,
    isOcResourceBusy,
    isOcNoNearbyOc,
    isOcGpsError,
    isOcGpsAcquiring,
    isOcNearbyOcFetching,
    isOcParticipating,
    nearbyOpenCampuses,
    pendingStampId,
    isQuestionnaireForm,
    isQuestionnaireSubmitting,
    isSearchResult,
    isCollegeDetail,
    selectedCollegeId,
    questionnaire,
    filterConditions,
    openFilterId,
    searchQuery,
    isAuthenticated,
    stampAlreadyAcquiredTodayMessage,
    noNearbyOcMessage,
  } = useMynaviStateMachine()

  // 選択値の名称表示用マスタ取得。各 Filter コンポーネントと同じキーでキャッシュを共有し、
  // 該当フィルターに選択がある場合のみ fetch する（未選択時の初期表示で無駄な API を発生させない）。
  const { data: collegeTypesData } = useQuery({
    queryKey: ['mynavi-college-types'],
    queryFn: () => api.guest.fetchMynaviCollegeTypes(),
    staleTime: 5 * 60 * 1000,
    enabled: filterConditions.collegeTypes.length > 0,
  })
  const collegeTypeLabelMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const t of collegeTypesData?.collegeTypes ?? []) map.set(t.value, t.label)
    return map
  }, [collegeTypesData])

  const { data: studyFieldsData } = useQuery({
    queryKey: ['mynavi-study-fields'],
    queryFn: () => api.guest.fetchMynaviStudyFields(false),
    staleTime: 5 * 60 * 1000,
    enabled: filterConditions.studyFieldIds.length > 0,
  })
  const studyFieldNameMap = useMemo(() => buildIdNameMap(studyFieldsData?.studyFields ?? []), [studyFieldsData])

  const { data: jobCategoriesData } = useQuery({
    queryKey: ['mynavi-job-categories'],
    queryFn: () => api.guest.fetchMynaviJobCategories(false),
    staleTime: 5 * 60 * 1000,
    enabled: filterConditions.jobCategoryIds.length > 0,
  })
  const jobCategoryNameMap = useMemo(() => buildIdNameMap(jobCategoriesData?.jobCategories ?? []), [jobCategoriesData])

  const emailRegistrationSkipKey = LOCAL_STORAGE_POPUP_HOOKPOINT_KEY('email-registration')
  const {
    send: emailSend,
    isEmailRegistrationPromptOpen,
    isEmailInputOpen,
    isEmailSending,
    isEmailVerificationOpen,
    isEmailVerifying,
    isEmailResending,
    isEmailCompleteOpen,
    emailRegistrationError,
    isEmailChangeMode,
  } = useMynaviHomeEmailStateMachine({ emailRegistrationSkipKey })

  // ─── フィルターオーバーレイのブラウザバック対応 ──────────────────────────
  // フィルターが開いたらダミー history エントリを積み、
  // ブラウザバック（popstate）で FILTER_CLOSE に変換する。
  const sendRef = useRef(send)
  sendRef.current = send

  useEffect(() => {
    if (openFilterId === null) return
    // state マーカーを付与してダミー履歴を積む。
    // cleanup 時にマーカーが残っている場合のみ back() を呼ぶことで、
    // ページ遷移中の unmount による意図しない遷移打ち消しを防ぐ。
    const MARKER = { mynaviFilter: true }
    let popstateFired = false
    window.history.pushState(MARKER, '', window.location.href)
    const handlePopState = () => {
      popstateFired = true
      sendRef.current({ type: 'FILTER_CLOSE' })
    }
    window.addEventListener('popstate', handlePopState)
    return () => {
      window.removeEventListener('popstate', handlePopState)
      // UIボタンで閉じた場合かつダミー履歴が現在の state に残っている場合のみ解消する
      if (!popstateFired && (window.history.state as Record<string, unknown>)?.mynaviFilter) {
        window.history.back()
      }
    }
  }, [openFilterId])

  const handleNavigationClick = (filterId: FilterId) => {
    send({ type: 'FILTER_OPEN', filterId })
  }

  // フィルター条件が1つでも設定されているか判定
  const hasFilterConditions =
    filterConditions.collegeTypes.length > 0 ||
    filterConditions.prefectures.length > 0 ||
    filterConditions.studyFieldIds.length > 0 ||
    filterConditions.jobCategoryIds.length > 0 ||
    filterConditions.eventDateFrom !== null ||
    filterConditions.eventDateTo !== null ||
    filterConditions.wishedOnly

  // 再レンダリングごとに新しいオブジェクトが生成されるのを防ぐ
  const today = useMemo(() => new Date(), [])

  // 学校名キーワード（300ms デバウンス）。件数取得とインクリメンタルサーチで共用する
  const [debouncedSchoolName] = useDebouncedValue(schoolName, 300)

  // フィルター条件またはキーワードのいずれかが指定されているか判定。
  // 学校名キーワードのみでも件数を表示・取得する（検索結果と件数を一致させるため #3215）
  const hasSearchConditions = hasFilterConditions || debouncedSchoolName.trim().length > 0

  // フィルター件数取得（早期 return の前に宣言してフック呼び出し順を固定）
  // 条件未設定時は fetch しない
  // buildFilterParams でeventDateTo等の正規化が行われるため、queryKeyにも同じ正規化後の値を使う。
  // 学校名キーワード（q）も含めることで検索結果（/filter）と件数（/filter/count）の母集合を揃える
  const filterParams = buildFilterParams(filterConditions, debouncedSchoolName)
  const { data: filterCountData } = useQuery({
    queryKey: ['mynavi', 'filter-count', filterParams],
    queryFn: () => {
      if (filterConditions.wishedOnly) {
        return api.auth.fetchMynaviCollegesFilterCount(filterParams)
      }
      return api.guest.fetchMynaviCollegesFilterCount(filterParams)
    },
    enabled: hasSearchConditions && (filterConditions.wishedOnly ? !!idToken && !tokenLoading : true),
    staleTime: 30_000,
    placeholderData: (prev: { count: number } | undefined) => prev,
  })

  // 学校名インクリメンタルサーチ（debouncedSchoolName を共用）
  const { data: incrementalData } = useQuery({
    queryKey: ['mynavi', 'colleges-incremental', debouncedSchoolName],
    queryFn: () => api.guest.fetchMynaviCollegesIncremental(debouncedSchoolName),
    enabled: debouncedSchoolName.length > 0,
    staleTime: 30_000,
  })

  // 学校詳細画面（XState: collegeDetail 状態）
  if (isCollegeDetail && selectedCollegeId) {
    return <CollegeDetail collegeId={selectedCollegeId} onBack={() => send({ type: 'NAVIGATE_BACK' })} />
  }

  // 検索結果画面（XState: searchResult 状態）
  if (isSearchResult) {
    return (
      <SearchResult
        filterConditions={filterConditions}
        searchQuery={searchQuery}
        onCollegeDetailOpen={(id) => send({ type: 'NAVIGATE_COLLEGE_DETAIL', collegeId: id })}
        onBack={() => send({ type: 'NAVIGATE_BACK' })}
      />
    )
  }

  const handleSearch = () => {
    send({ type: 'NAVIGATE_SEARCH_RESULT', query: schoolName })
  }

  // UI 側で openCampusItem.stamp.id を取り出して送信する
  // ⚠️ stamp.id は nullable (MynaviNearbyStampSchema.id: z.number().nullable()) のため、
  // null の場合はスタンプなし OC として参加ボタンを非活性化し、このイベントを送信しないこと
  const handleOcParticipateRequest = (stampId: number) => {
    send({ type: 'OC_PARTICIPATE_REQUEST', stampId })
  }

  const navigationItems: MyNaviNavigationItem[] = [
    {
      icon: <IconBuilding size={20} stroke={1.5} />,
      label: '学校の種類を選択',
      selectedValue:
        filterConditions.collegeTypes.length > 0
          ? buildNamesLabel(filterConditions.collegeTypes.map((v) => collegeTypeLabelMap.get(v) ?? v))
          : undefined,
      onClick: () => handleNavigationClick('schoolType'),
    },
    {
      icon: <IconMapPin size={20} stroke={1.5} />,
      label: 'エリア・都道府県を選択',
      selectedValue:
        filterConditions.prefectures.length > 0 ? buildNamesLabel(filterConditions.prefectures) : undefined,
      onClick: () => handleNavigationClick('area'),
    },
    {
      icon: <IconSchool size={20} stroke={1.5} />,
      label: '学べる学問を選択',
      selectedValue:
        filterConditions.studyFieldIds.length > 0
          ? buildNamesLabel(filterConditions.studyFieldIds.map((id) => studyFieldNameMap.get(id) ?? `#${id}`))
          : undefined,
      onClick: () => handleNavigationClick('subject'),
    },
    {
      icon: <IconBriefcase size={20} stroke={1.5} />,
      label: '目指せる職種を選択',
      selectedValue:
        filterConditions.jobCategoryIds.length > 0
          ? buildNamesLabel(filterConditions.jobCategoryIds.map((id) => jobCategoryNameMap.get(id) ?? `#${id}`))
          : undefined,
      onClick: () => handleNavigationClick('occupation'),
    },
    {
      icon: <IconCalendar size={20} stroke={1.5} />,
      label: '開催期間を選択',
      selectedValue: filterConditions.eventDateFrom
        ? filterConditions.eventDateTo
          ? `${filterConditions.eventDateFrom}〜${filterConditions.eventDateTo}`
          : filterConditions.eventDateFrom
        : undefined,
      onClick: () => handleNavigationClick('period'),
    },
  ]

  return (
    <>
      {/* フィルターオーバーレイ */}
      {openFilterId !== null && (
        <div className="fixed inset-0 z-50 bg-white overflow-y-auto">
          {openFilterId === 'schoolType' && (
            <FilterSchoolType
              initialCollegeTypes={filterConditions.collegeTypes}
              onSelect={(collegeTypes) => send({ type: 'FILTER_SELECT_COLLEGE_TYPES', collegeTypes })}
              onClear={() => send({ type: 'FILTER_CLEAR', filterId: 'schoolType' })}
              onClose={() => send({ type: 'FILTER_CLOSE' })}
            />
          )}
          {openFilterId === 'area' && (
            <FilterArea
              initialPrefectures={filterConditions.prefectures}
              onSelect={(prefectures) => send({ type: 'FILTER_SELECT_AREA', prefectures })}
              onClear={() => send({ type: 'FILTER_CLEAR', filterId: 'area' })}
              onClose={() => send({ type: 'FILTER_CLOSE' })}
            />
          )}
          {openFilterId === 'subject' && (
            <FilterStudyField
              initialStudyFieldIds={filterConditions.studyFieldIds}
              onSelect={(studyFieldIds) => send({ type: 'FILTER_SELECT_STUDY_FIELDS', studyFieldIds })}
              onClear={() => send({ type: 'FILTER_CLEAR', filterId: 'subject' })}
              onClose={() => send({ type: 'FILTER_CLOSE' })}
            />
          )}
          {openFilterId === 'occupation' && (
            <FilterJobCategory
              initialJobCategoryIds={filterConditions.jobCategoryIds}
              onSelect={(jobCategoryIds) => send({ type: 'FILTER_SELECT_JOB_CATEGORIES', jobCategoryIds })}
              onClear={() => send({ type: 'FILTER_CLEAR', filterId: 'occupation' })}
              onClose={() => send({ type: 'FILTER_CLOSE' })}
            />
          )}
          {openFilterId === 'period' && (
            <FilterPeriod
              initialEventDateFrom={filterConditions.eventDateFrom}
              initialEventDateTo={filterConditions.eventDateTo}
              today={today}
              onSelect={(from, to) => send({ type: 'FILTER_SELECT_PERIOD', from, to })}
              onClear={() => send({ type: 'FILTER_CLEAR', filterId: 'period' })}
              onClose={() => send({ type: 'FILTER_CLOSE' })}
            />
          )}
        </div>
      )}

      {/* OC 参加確認ダイアログ */}
      {isOcConfirmDialog && (
        <MyNaviDialog
          isOpen={isOcConfirmDialog}
          onClose={() => send({ type: 'OC_PARTICIPATE_CANCEL' })}
          message={(() => {
            const collegeName = nearbyOpenCampuses.find((oc) => oc.stamp.id === pendingStampId)?.college.name
            return collegeName
              ? `${collegeName}のオープンキャンパスに参加していますか？`
              : 'オープンキャンパスに参加していますか？'
          })()}
          buttons={[
            {
              label: '参加する',
              variant: 'blue',
              onClick: () => send({ type: 'OC_PARTICIPATE_CONFIRM' }),
            },
          ]}
        />
      )}

      {/* 近くのOC選択ダイアログ */}
      <MyNaviNearbyOcDialog
        isOpen={isOcNearbyOcDialog}
        onClose={() => send({ type: 'OC_PARTICIPATE_CANCEL' })}
        openCampuses={nearbyOpenCampuses}
        onSelect={(oc) => {
          if (oc.stamp.id != null) {
            send({ type: 'OC_NEARBY_SELECT', stampId: oc.stamp.id })
          }
        }}
      />

      {/* GPS エラーダイアログ */}
      <MyNaviDialog
        isOpen={isOcGpsError}
        onClose={() => send({ type: 'OC_GPS_ERROR_CLOSE' })}
        message={'現在地を取得できませんでした。\nお使いのブラウザの設定を確認し、位置情報を許可してください。'}
        buttons={[
          {
            label: '閉じる',
            variant: 'white',
            onClick: () => send({ type: 'OC_GPS_ERROR_CLOSE' }),
          },
        ]}
      />

      {/* 近くにOCなしダイアログ */}
      <MyNaviDialog
        isOpen={isOcNoNearbyOc}
        onClose={() => send({ type: 'OC_NO_NEARBY_CLOSE' })}
        message={noNearbyOcMessage ?? '参加可能な\nオープンキャンパスが\nありません'}
        buttons={[
          {
            label: '閉じる',
            variant: 'white',
            onClick: () => send({ type: 'OC_NO_NEARBY_CLOSE' }),
          },
        ]}
      />

      {/* 参加済みダイアログ */}
      <MyNaviDialog
        isOpen={isOcAlreadyParticipated}
        onClose={() => send({ type: 'OC_ALREADY_PARTICIPATED_CLOSE' })}
        message={'すでに参加しています。'}
        buttons={[
          {
            label: '閉じる',
            variant: 'white',
            onClick: () => send({ type: 'OC_ALREADY_PARTICIPATED_CLOSE' }),
          },
        ]}
      />

      {/* 1日1スタンプ制限ダイアログ */}
      <MyNaviDialog
        isOpen={isOcStampAlreadyAcquiredToday}
        onClose={() => send({ type: 'OC_STAMP_ALREADY_ACQUIRED_TODAY_CLOSE' })}
        message={stampAlreadyAcquiredTodayMessage ?? '本日のスタンプはすでに獲得済みです。'}
        buttons={[
          {
            label: '閉じる',
            variant: 'white',
            onClick: () => {},
          },
        ]}
      />

      {/* 混み合いダイアログ（409 RESOURCE_BUSY）。閉じると参加確認ダイアログへ戻る */}
      <MyNaviDialog
        isOpen={isOcResourceBusy}
        onClose={() => send({ type: 'OC_RESOURCE_BUSY_CLOSE' })}
        message={t('error.resource_busy', '混み合っています。少し時間をおいて、もう一度お試しください。')}
        buttons={[
          {
            label: t('common.close', '閉じる'),
            variant: 'white',
            onClick: () => {},
          },
        ]}
      />

      {/* アンケートフォームは raundLifeCycle の中で表示するため、このコンポーネントでは表示しない */}

      <div>
        {/* メインビジュアル（キャンペーントップ画像）＋ヘッダーオーバーレイ */}
        {topImage ? (
          <div className="relative w-full h-auto overflow-hidden">
            <img src={topImage} alt="マイナビ進学" className="w-full h-full object-cover" />
            {/* ハンバーガーアイコン（右）をメインビジュアルにオーバーレイ */}
            <div className="absolute inset-x-0 top-0 flex items-center justify-end px-4 h-10 z-10">
              <button type="button" aria-label="メニューを開く" className="p-1" onClick={() => setIsMenuOpen(true)}>
                <IconMenu2 size={24} color="black" />
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-end px-4 h-10">
            <button type="button" aria-label="メニューを開く" className="p-1" onClick={() => setIsMenuOpen(true)}>
              <IconMenu2 size={24} color="black" />
            </button>
          </div>
        )}

        {/* ログインボタン */}
        <div className="py-3 px-2">
          {tokenLoading || (!!idToken && userSheetsLoading) ? (
            <MyNaviPointDisplay loading={true} points={0} onHistoryClick={() => {}} onExchangeClick={() => {}} />
          ) : idToken ? (
            <MyNaviPointDisplay
              points={userSheetsData?.sheets[0]?.meta?.totalPoint ?? 0}
              onHistoryClick={() => router.push(gen('/mynavi/point-history'))}
              onExchangeClick={() => router.push(gen('/mynavi/exchange'))}
            />
          ) : (
            <MyNaviButton label="ログイン" showIcon={false} onClick={() => router.push(loginUrl)} />
          )}
        </div>
        {/* 検索セクション（Figma: 検索枠 bg rgba(17,159,207,0.23)） */}
        <div className="bg-[rgba(17,159,207,0.23)] px-3 py-4 flex flex-col gap-3">
          <MyNaviTypography variant="search-hint" className="text-center leading-none m-0">
            オープンキャンパスを探す！
          </MyNaviTypography>

          <MyNaviInput
            value={schoolName}
            onChange={setSchoolName}
            onSearch={handleSearch}
            data={incrementalData?.colleges.map((c) => c.name) ?? []}
          />

          {/* 詳細検索枠: 白背景コンテナ（Figma: bg=#fbfbfb, rounded-5px） */}
          {/* ナビゲーション・チェックボックス・検索ボタンを内包 */}
          <div className="bg-[#fbfbfb] rounded-[5px] pt-4 overflow-hidden">
            {/* ナビ＋チェックボックス（横20px padding） */}
            <div className="px-5 flex flex-col gap-3">
              <MyNaviVerticalNavigation items={navigationItems} />
              <div className={'pl-2'}>
                <Checkbox
                  checked={filterConditions.wishedOnly}
                  onChange={(checked) => {
                    if (checked && !idToken) {
                      router.push(loginUrl)
                      return
                    }
                    send({ type: 'FILTER_SELECT_WISHED_ONLY', wishedOnly: checked })
                  }}
                  label="「行きたい」にチェックしたもの"
                  style={
                    {
                      '--mantine-color-primary-5': MYNAVI_PRIMARY,
                      '--checkbox-icon-color': '#ffffff',
                    } as React.CSSProperties
                  }
                />
              </div>
            </div>
            {/* 検索ボタン + クリアボタン: チェックボックスとの余白21px（Figma計測値）、横8px padding */}
            <div className="pt-[21px] px-2 pb-[10px] flex items-end gap-2">
              {/* 検索ボタン（Figma: 左寄せ・横幅を flex-1 で拡張） */}
              <div className="flex-1">
                <MyNaviButton
                  onClick={handleSearch}
                  resultCount={hasSearchConditions ? filterCountData?.count : undefined}
                />
              </div>
              {/* クリアボタン（Figma: 右配置・幅96px） */}
              <button
                type="button"
                onClick={() => send({ type: 'FILTER_CLEAR_ALL' })}
                disabled={!hasFilterConditions}
                style={{
                  width: '96px',
                  height: '38px',
                  border: '1px solid #686868',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  color: hasFilterConditions ? '#686868' : '#cccccc',
                  fontSize: '16px',
                  fontFamily: "'BIZ UDPGothic', sans-serif",
                  cursor: hasFilterConditions ? 'pointer' : 'default',
                  flexShrink: 0,
                }}
              >
                クリア
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* 参加登録 FAB（認証済みの場合のみ・画面右下に固定） */}
      {/* 最下部までスクロール時はクリアボタンと重ならないよう上方へ退避（84px）、それ以外は 24px */}
      {isAuthenticated && (
        <div
          style={{
            position: 'fixed',
            bottom: isScrolledToBottom ? '84px' : '24px',
            right: '16px',
            zIndex: 40,
            transition: 'bottom 0.2s ease',
          }}
        >
          <MyNaviParticipateButton
            onClick={() => send({ type: 'OC_REGISTER_TAPPED' })}
            loading={isOcGpsAcquiring || isOcNearbyOcFetching || isOcParticipating}
          />
        </div>
      )}
      <MyNaviMenuDrawer
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        apiMenuItems={apiMenuItems}
        onEmailRegistration={() => emailSend({ type: 'EMAIL_CHANGE_BUTTON_PRESSED' })}
      />
      <EmailRegistrationDialogs
        isEmailRegistrationPromptOpen={isEmailRegistrationPromptOpen}
        isEmailInputOpen={isEmailInputOpen}
        isEmailSending={isEmailSending}
        isEmailVerificationOpen={isEmailVerificationOpen}
        isEmailVerifying={isEmailVerifying}
        isEmailResending={isEmailResending}
        isEmailCompleteOpen={isEmailCompleteOpen}
        emailRegistrationError={emailRegistrationError}
        isEmailChangeMode={isEmailChangeMode}
        onRegister={() => emailSend({ type: 'EMAIL_REGISTRATION_PROCEED' })}
        onSkip={() => emailSend({ type: 'EMAIL_REGISTRATION_SKIP' })}
        onEmailSubmit={(email) => emailSend({ type: 'EMAIL_INPUT_SUBMIT', email })}
        onEmailCancel={() => emailSend({ type: 'EMAIL_INPUT_BACK' })}
        onCodeSubmit={(code) => emailSend({ type: 'CODE_INPUT_SUBMIT', code })}
        onCodeResend={() => emailSend({ type: 'CODE_RESEND' })}
        onCodeBack={() => emailSend({ type: 'EMAIL_INPUT_BACK' })}
        onComplete={() => emailSend({ type: 'EMAIL_REGISTRATION_CLOSE' })}
      />
    </>
  )
}
